<template>
  <div class="d-flex mt-1 mb-2 justify-content-center">
    <div
      :class="[
        'card-wrapper',
        { fill, alertMode, 'card-wrapper-flat': flatStyle },
      ]"
    >
      <div
        class="title-container"
        :class="{ open: isOpen || !collapsable, alertMode, 'has-description': titleDescription }"
        :style="
          flatStyle
            ? { padding: '0', height: '40px' }
            : titleDescription
              ? { padding: '1rem 0.69rem', height: 'auto', minHeight: '50px' }
              : { padding: '1rem 0.69rem', height: '50px' }
        "
        @click="onClick"
      >
        <div class="title-content-wrapper">
          <div class="title-text" :class="{ alertMode }">
            <i v-if="titleIcon" :class="titleIcon" />
            <span v-if="rtfTitle" v-html="title"></span>
            <span
              v-else
              :style="`font-weight: ${
                boldTitle ? 'bold' : 'initial'
              }; font-size: ${titleFontSize || '16px'}`"
            >
              {{ title }}
            </span>
          </div>
          <div class="title-description" v-if="titleDescription">
            {{ titleDescription }}
          </div>
        </div>
        <div class="icon-wrapper" v-if="collapsable">
          <i
            v-if="!isOpen"
            class="fas fa-chevron-down"
            style="font-size: 15px"
          />
          <i v-else class="fas fa-chevron-up" style="font-size: 15px" />
        </div>
      </div>

      <transition name="slide-down">
        <div
          v-if="isOpen || !collapsable"
          class="content-block"
          :style="contentStyle"
        >
          <slot></slot>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from "vue";

export default defineComponent({
  name: "CollapseCard",
  props: {
    title: {
      type: String,
      default: "",
    },
    titleIcon: {
      type: String,
      default: "",
    },
    titleDescription: String,
    fill: {
      type: Boolean,
      default: false,
    },
    collapsable: {
      type: Boolean,
      default: true,
    },
    boldTitle: {
      type: Boolean,
      default: false,
    },
    alertMode: {
      type: Boolean,
      default: false,
    },
    rtfTitle: {
      type: Boolean,
      default: false,
    },
    titleFontSize: {
      type: String,
      default: "",
    },
    defaultOpen: {
      type: Boolean,
      default: false,
    },
    flatStyle: {
      type: Boolean,
      default: false,
    },
    contentStyle: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const isOpen = ref<boolean>(props.defaultOpen);

    return {
      isOpen,
    };
  },
  methods: {
    onClick() {
      this.isOpen = !this.isOpen;
    },
  },
});
</script>

<style lang="scss" scoped>
@import "@/assets/scss/_constants.scss";
$pj-color-light-alert-red: #fccfd9;
$pj-color-alert-red: #e11d48;
.card-wrapper {
  min-height: 50px;
  background-color: #fbfbfb;
  border: 1px solid $pj-color-light-gray;
  border-radius: 11px;
  &.fill {
    width: 100%;
  }
  &.alertMode {
    background-color: $pj-color-light-alert-red;
    border: 1px solid $pj-color-alert-red;
  }
}
.card-wrapper-flat {
  background: #f7f7f7;
  border: none;
  border-radius: 8px;
  box-shadow: none;
  min-height: 50px;
  padding: 8px 16px;
}
.title-container {
  padding: 1rem 0.69rem;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  background: transparent;
  border-bottom: 1px solid $pj-color-light-gray;
  &.open {
    border-bottom: 1px solid $pj-color-light-gray;
  }
  &.alertMode {
    border-bottom: none;
  }
  &.has-description {
    height: auto;
    min-height: 50px;
  }
}
.title-content-wrapper {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  justify-content: center;
}
.title-text {
  align-items: center;
  display: flex;
  font-family: Inter, serif;
  color: #444;
  font-size: 18px;
  border: none;
  background-color: transparent;
  font-weight: 500;
  &.alertMode {
    color: $pj-color-alert-red;
  }
}
.icon-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  color: #222;
  width: 24px;
  height: 24px;
  font-size: 20px;
  i {
    margin: 0;
  }
}
.content-block {
  font-size: 16px;
  color: #444;
  background: transparent;
}

@keyframes slideDown {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(100%);
  }
}

.slide-down-enter-active,
.slide-down-leave-active {
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.3s;
  overflow: hidden;
}
.slide-down-enter-from,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}
.slide-down-enter-to,
.slide-down-leave-from {
  max-height: 500px; // Adjust as needed for your content
  opacity: 1;
}
.title-description {
  color: #8B8B8B;
  font-family: Inter, serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
  width: 100%;
  margin-top: 0.25rem;
}
</style>
